/**
 * Pure cookie helpers (`document.cookie` wrapper).
 *
 * - Cookies named `csr` and `user` are stored as Base64-encoded JSON
 *   (legacy contract from apps/b2b-ui/common/services/auth/auth.service.js,
 *   lines ~21 and 74-88). The reader transparently decodes them.
 * - All other cookies are URL-encoded JSON (or plain strings).
 */

const ENCODED_COOKIES = ['csr', 'user'] as const;

function readRawCookie(name: string): string | null {
  const target = name + '=';
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const entry of cookies) {
    if (entry.indexOf(target) === 0) {
      return entry.substring(target.length);
    }
  }
  return null;
}

export function getCookieValue<T = unknown>(name: string): T | null {
  const raw = readRawCookie(name);
  if (raw == null || raw === '') {
    return null;
  }

  // All cookies set by Rails are URL-encoded in the Set-Cookie header
  // (e.g. base64 padding `=` becomes `%3D`). Decode that first so the value
  // matches the contract documented by the legacy `js-cookie` library used
  // before this migration.
  let value: string = raw;
  try {
    value = decodeURIComponent(value);
  } catch {
    // Not valid URL encoding — keep as-is.
  }

  // For `csr` and `user` the legacy code additionally base64-decodes the
  // value before JSON-parsing it.
  if ((ENCODED_COOKIES as readonly string[]).includes(name)) {
    try {
      value = atob(value);
    } catch {
      // Not valid base64 — keep the URL-decoded value.
    }
  }

  // If the value looks like JSON, try to parse it.
  if (value.length > 0 && value.charAt(0) === '{') {
    try {
      return JSON.parse(value) as T;
    } catch {
      // Fall through and return as string.
    }
  }

  return value as unknown as T;
}

export function setCookieValue(name: string, value: unknown): void {
  const serialized = encodeURIComponent(JSON.stringify(value));
  document.cookie = `${name}=${serialized}; path=/`;
}

export function removeCookie(name: string): void {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}
